const REQUIRED_MANIFEST_FIELDS = ["schemaVersion", "id", "model", "variant", "license", "canonicalWorkload"];
const DEFAULT_MANIFEST_URL = new URL("../models/flux2-klein-runtime.manifest.json", import.meta.url).href;

export async function probeFluxRuntime({ manifestUrl = DEFAULT_MANIFEST_URL, onEvent = () => {} } = {}) {
  onEvent("Validating the local FLUX.2 Klein model manifest.");
  const response = await fetch(manifestUrl, { cache: "no-store" });
  if (!response.ok) throw new Error(`Model manifest returned HTTP ${response.status}.`);
  const manifest = await response.json();
  validateManifest(manifest);

  const adapter = globalThis.SHAREMYAI_FLUX_ADAPTER;
  const runtimeAvailable = Boolean(adapter && typeof adapter.initialize === "function" && typeof adapter.render === "function");
  onEvent(runtimeAvailable
    ? `Model runtime detected: ${adapter.id || "custom adapter"}.`
    : "Model manifest is valid; the audited runtime and weight bundle are not installed.");

  return {
    manifestValid: true,
    manifest: {
      id: manifest.id,
      model: manifest.model,
      variant: manifest.variant,
      license: manifest.license,
      canonicalWorkload: manifest.canonicalWorkload,
      sources: manifest.sources
    },
    runtimeAvailable,
    runtimeId: adapter?.id || null,
    canonicalRender: null
  };
}

export function validateManifest(manifest) {
  if (!manifest || typeof manifest !== "object") throw new TypeError("Model manifest must be an object.");
  const missing = REQUIRED_MANIFEST_FIELDS.filter((field) => !manifest[field]);
  if (missing.length) throw new Error(`Model manifest is missing: ${missing.join(", ")}.`);
  const workload = manifest.canonicalWorkload;
  if (!Number.isInteger(workload.steps) || workload.steps < 1) throw new Error("Canonical steps must be a positive integer.");
  if (!Number.isInteger(workload.width) || !Number.isInteger(workload.height)) throw new Error("Canonical dimensions must be integers.");
  if (!workload.prompt || !Number.isInteger(workload.seed)) throw new Error("Canonical prompt and integer seed are required.");
  return true;
}

export async function runCanonicalRender(modelProbe, { onProgress = () => {}, signal } = {}) {
  if (!modelProbe?.runtimeAvailable) throw new Error("No FLUX runtime adapter is installed.");
  const adapter = globalThis.SHAREMYAI_FLUX_ADAPTER;
  signal?.throwIfAborted();
  await adapter.initialize({ manifest: modelProbe.manifest, onProgress, signal });
  signal?.throwIfAborted();
  const startedAt = performance.now();
  const output = await adapter.render({ ...modelProbe.manifest.canonicalWorkload, onProgress, signal });
  if (!output?.image || !output?.outputHash) throw new Error("The runtime returned no image or output hash.");
  return {
    success: true,
    durationMs: Number((performance.now() - startedAt).toFixed(2)),
    outputHash: output.outputHash,
    runtimeId: adapter.id || "custom-adapter",
    image: output.image
  };
}
